import React, { useEffect, useRef, useState } from "react";
import { StoryChoice, StoryScene } from "../../../story/storyTypes";

type StoryPageProps = {
  scene: StoryScene;
  storyTitle: string;
  feedback?: string;
  isShaking: boolean;
  onChoice: (choice: StoryChoice) => void;
};

const splitParagraphs = (text?: string) =>
  (text || "")
    .split(/\n\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

export function StoryPage({
  scene,
  storyTitle,
  feedback,
  isShaking,
  onChoice,
}: StoryPageProps) {
  const layout = scene.layout || "default";
  const hasChoices = Boolean(scene.choices?.length);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const paragraphs = splitParagraphs(scene.text);

  useEffect(() => {
    setIsPlaying(false);
    const audio = audioRef.current;

    return () => {
      audio?.pause();
    };
  }, [scene.id]);

  const playAudio = () => {
    if (!audioRef.current) {
      return;
    }

    audioRef.current.currentTime = 0;
    audioRef.current.play();
  };

  const toggleAudio = () => {
    if (!audioRef.current) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    audioRef.current.play();
    setIsPlaying(true);
  };

  if (layout === "cover") {
    return (
      <main className="story-page story-page-cover" aria-label={storyTitle}>
        <section className="story-cover" aria-label="Book cover">
          <img className="story-cover-image" src={scene.image} alt={storyTitle} />
        </section>
      </main>
    );
  }

  if (layout === "spread") {
    return (
      <main className="story-page story-page-spread" aria-label={storyTitle}>
        <section className="story-book-text" aria-label="Story text">
          <div className="story-book-text-inner">
            {paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </section>
        <section className="story-book-image" aria-label="Story picture">
          <img
            className="story-book-photo"
            src={scene.image}
            alt={`${storyTitle} ${scene.id}`}
          />
        </section>
      </main>
    );
  }

  if (layout === "lyrics") {
    return (
      <main className="story-page story-page-lyrics" aria-label={scene.heading || storyTitle}>
        <section className="story-lyrics" aria-label="Song lyrics">
          {scene.heading && <h2 className="story-lyrics-title">{scene.heading}</h2>}
          {scene.lyrics?.map((section, index) => (
            <div className="story-lyric-section" key={`${section.heading}-${index}`}>
              <h3>{section.heading}</h3>
              {section.lines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ))}
          {scene.text && <p className="story-lyrics-note">{scene.text}</p>}
        </section>
        <aside className="story-song-panel">
          {scene.image && (
            <img className="story-song-cover" src={scene.image} alt="" />
          )}
          {scene.audio && (
            <>
              <audio
                ref={audioRef}
                src={scene.audio}
                preload="auto"
                onEnded={() => setIsPlaying(false)}
              />
              <button
                className="story-song-button"
                type="button"
                onClick={toggleAudio}
              >
                {isPlaying ? "暫停" : "播放歌曲"}
              </button>
            </>
          )}
        </aside>
      </main>
    );
  }

  return (
    <main
      className={`story-page ${hasChoices ? "story-page-with-menu" : ""} ${
        isShaking ? "story-page-shake" : ""
      }`}
      aria-label={storyTitle}
    >
      <section
        className={`story-illustration ${scene.audio ? "story-illustration-audio" : ""}`}
        aria-label="Story picture"
        onClick={scene.audio ? playAudio : undefined}
      >
        {scene.video ? (
          <video
            className="story-media"
            src={scene.video}
            poster={scene.image}
            controls
            playsInline
          />
        ) : (
          <img className="story-media" src={scene.image} alt={`${storyTitle} ${scene.id}`} />
        )}
        {scene.audio && (
          <>
            <audio ref={audioRef} src={scene.audio} preload="auto" />
            <div className="story-audio-hint">按一下播放聲音</div>
          </>
        )}
      </section>

      {hasChoices && (
        <aside className="story-choice-panel" aria-label="Choose an answer">
          <div className="story-choice-panel-card">
            <p className="story-choice-title">Choose one</p>
            <div className="story-choice-list">
              {scene.choices?.map((choice) => (
                <button
                  key={`${scene.id}-${choice.label}`}
                  className="story-choice-button"
                  type="button"
                  onClick={() => onChoice(choice)}
                >
                  {choice.label}
                </button>
              ))}
            </div>
            {feedback && (
              <div className="story-feedback" role="alert">
                {feedback}
              </div>
            )}
          </div>
        </aside>
      )}

      {scene.text && (
        <section className="story-panel">
          <div className="story-panel-content">
            <p className="story-narration">{scene.text}</p>
          </div>
        </section>
      )}
    </main>
  );
}
